import { FC, useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { ParameterMapping, ParseNodeData, WorkflowNodeData } from '../types'
import { themeColor, xxllncColor } from '../../../layout/theme/color'
import { getJson, getUrl } from '../../../providers/data'
import { PortalParserOut } from '../../../types/apiRequests'
import ExpressionTextField from '../autocomplete/ExpressionTextField'
import { valueToText } from './ParameterValueField'

interface ParseConfigProps {
  data: ParseNodeData
  onUpdate: (data: Partial<WorkflowNodeData>) => void
}

const ParseConfig: FC<ParseConfigProps> = ({ data, onUpdate }) => {
  const translate = useTranslate()
  const [parsers, setParsers] = useState<PortalParserOut[]>([])
  const [loading, setLoading] = useState(true)
  const [newExtraKey, setNewExtraKey] = useState('')

  useEffect(() => {
    getJson<PortalParserOut[]>(`${getUrl()}/portal/parsers`)
      .then(setParsers)
      .catch(() => setParsers([]))
      .finally(() => setLoading(false))
  }, [])

  const selected = parsers.find((p) => p.parser_id === data.parserId)

  const handleAddExtra = () => {
    const key = newExtraKey.trim()
    if (!key) return
    const mapping: ParameterMapping = { source: 'expression', value: '' }
    onUpdate({ extra: { ...data.extra, [key]: mapping } })
    setNewExtraKey('')
  }

  const handleRemoveExtra = (key: string) => {
    const updated = { ...data.extra }
    // eslint-disable-next-line security/detect-object-injection
    delete updated[key]
    onUpdate({ extra: updated })
  }

  const handleUpdateExtra = (key: string, value: string) => {
    onUpdate({
      extra: {
        ...data.extra,
        // eslint-disable-next-line security/detect-object-injection
        [key]: { source: 'expression', value },
      },
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label={translate('workflow.config.label')}
        value={data.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        size="small"
        fullWidth
      />

      <Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: themeColor.slate60, mb: 0.5, display: 'block' }}
        >
          {translate('workflow.config.parser')}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <TextField
            select
            value={data.parserId}
            onChange={(e) => onUpdate({ parserId: e.target.value })}
            size="small"
            fullWidth
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" disabled>
              {translate('workflow.config.parserPlaceholder')}
            </MenuItem>
            {parsers.map((p) => (
              <MenuItem key={p.parser_id} value={p.parser_id}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>
                    {p.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: themeColor.slate60, fontSize: 11, display: 'block' }}
                  >
                    {p.parser_id} · {p.input_format} → {p.output_kind}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        )}
        {selected && (
          <Typography
            variant="caption"
            sx={{ color: themeColor.slate60, mt: 0.5, display: 'block', lineHeight: 1.4 }}
          >
            {selected.description}
          </Typography>
        )}
        {!loading && parsers.length === 0 && (
          <Typography
            variant="caption"
            sx={{ color: xxllncColor.red100, mt: 0.5, display: 'block', lineHeight: 1.4 }}
          >
            {translate('workflow.config.parserNoneEnabled')}
          </Typography>
        )}
      </Box>

      <Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: themeColor.slate60, mb: 0.5, display: 'block' }}
        >
          {translate('workflow.config.parserMode')}
        </Typography>
        <ToggleButtonGroup
          value={data.mode}
          exclusive
          onChange={(_e, val: string | null) =>
            val && onUpdate({ mode: val as 'parse' | 'requirements' })
          }
          size="small"
          fullWidth
        >
          <ToggleButton value="parse" sx={{ textTransform: 'none' }}>
            {translate('workflow.config.parserModeParse')}
          </ToggleButton>
          <ToggleButton value="requirements" sx={{ textTransform: 'none' }}>
            {translate('workflow.config.parserModeRequirements')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ExpressionTextField
        label={translate('workflow.config.parserInputSource')}
        value={data.inputSource}
        onChange={(e) => onUpdate({ inputSource: e.target.value })}
        size="small"
        fullWidth
        placeholder="{{ data_node_id.match }}"
        helperText={translate('workflow.config.parserInputSourceHelp')}
        InputProps={{ sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }}
      />

      <Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: themeColor.slate60, mb: 0.5, display: 'block' }}
        >
          {translate('workflow.config.parserExtra')}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: themeColor.slate60, mb: 1, display: 'block', lineHeight: 1.4 }}
        >
          {translate('workflow.config.parserExtraHelp')}
        </Typography>
        {Object.entries(data.extra || {}).map(([key, mapping]) => (
          <Box
            key={key}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
          >
            <Chip
              label={key}
              size="small"
              sx={{
                height: 32,
                fontSize: 12,
                fontFamily: '"SF Mono", "Fira Code", monospace',
                background: themeColor.slate05,
                flexShrink: 0,
              }}
            />
            <ExpressionTextField
              size="small"
              fullWidth
              value={valueToText(mapping.value)}
              onChange={(e) => handleUpdateExtra(key, e.target.value)}
              placeholder="{{ search_folder.uuid }}"
              InputProps={{
                sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 },
              }}
            />
            <IconButton
              size="small"
              onClick={() => handleRemoveExtra(key)}
              sx={{ flexShrink: 0 }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            value={newExtraKey}
            onChange={(e) => setNewExtraKey(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddExtra()
              }
            }}
            placeholder={translate('workflow.config.parserExtraKeyPlaceholder')}
            InputProps={{
              sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 },
            }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddExtra}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            {translate('ra.action.add')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default ParseConfig
